import { toCents, fromCents, allocateDailyAmounts, calendarDate, countCoverageDays } from './financialMath.js';

const key = t => String(t.recurringRuleId ?? '') + ':' +
  (t.coveragePeriod?.start || t.date || '');

const valid = t => t && !t.isDeleted && (t.type === 'expense' || t.type === 'income') &&
  Number.isFinite(Number(t.amount));

const asMoney = cents => fromCents(cents);

function calendarDays(start, end) {
  const first = Date.parse(start + 'T00:00:00.000Z');
  const count = countCoverageDays(start, end);
  if (count > 3660) throw new Error('Coverage period is unexpectedly long');
  return Array.from({ length: count }, (_, i) =>
    new Date(first + i * 86400000).toISOString().slice(0, 10));
}

/**
 * Cash = explicit transaction records only. Generated day allocations are NOT
 * proof of money changing hands. Accrual = daily allocations (or virtual daily
 * entries for legacy single-period records), never the whole amount as well.
 * IOU-linked transfers are balance-sheet movements, not operating income/expense.
 */
export function accountingEntries(transactions, mode = 'accrual', ious = []) {
  if (mode !== 'cash' && mode !== 'accrual') throw new Error('Unknown accounting mode');
  const linked = new Set(ious.filter(i => !i.isDeleted && i.linkedTransactionId != null)
    .map(i => String(i.linkedTransactionId)));
  const base = transactions.filter(t => valid(t) && !t.linkedIOUId && !t.iouId &&
    !linked.has(String(t.id)));
  if (mode === 'cash') return base.filter(t => !t.isDailyBreakdown);

  // A legacy recurring payment is the source of truth for its accrual period.
  // If its daily rows are missing or incomplete, regenerate the ENTIRE period
  // virtually. Mixing partial persisted allocations with reconstructed ones
  // could silently overstate or understate the total.
  const payments = base.filter(t => t.isRecurring && !t.isDailyBreakdown);
  const coveredKeys = new Set(payments.map(key));
  const result = base.filter(t => !t.isRecurring || (
    t.isDailyBreakdown && !coveredKeys.has(key(t))
  ));
  for (const payment of payments) {
    const start = calendarDate(payment.coveragePeriod?.start || payment.date);
    const end = calendarDate(payment.coveragePeriod?.end || payment.date);
    if (!start || !end) continue;
    const days = calendarDays(start, end);
    const expected = allocateDailyAmounts(payment.amount, days.length);
    const existing = base.filter(t => t.isDailyBreakdown && key(t) === key(payment));
    const existingByDate = new Map(existing.map(t => [calendarDate(t.date), t]));
    const complete = existing.length === days.length && days.every((date, i) =>
      existingByDate.has(date) && toCents(existingByDate.get(date).amount) === toCents(expected[i]));
    days.forEach((date, i) => {
      if (complete) {
        result.push(existingByDate.get(date));
      } else {
        result.push({ ...payment, date, amount: expected[i], isDailyBreakdown: true,
          isVirtualAllocation: true, sourceTransactionId: payment.id });
      }
    });
  }
  return result;
}

export function selectAccountingEntries(entries, { start, end, asOf = new Date(),
  view = 'current', mode = 'accrual' } = {}) {
  const from = start ? calendarDate(start) : '0000-01-01';
  const to = end ? calendarDate(end) : '9999-12-31';
  const today = calendarDate(asOf);
  return entries.filter(t => {
    const date = calendarDate(t.date);
    if (!date || date < from || date > to) return false;
    if (view === 'future') return date > today;
    // Scheduled accruals describe daily costs; a scheduled cash transaction
    // does not become paid merely because its date passes.
    return date <= today && (mode === 'accrual' && t.isDailyBreakdown ||
      (t.status === 'posted' && (!t.isRecurring || t.isDailyBreakdown || t.cashConfirmed === true)));
  });
}

export function summarizeTransactions(entries) {
  let income = 0, expenses = 0;
  for (const t of entries) {
    if (t.type === 'income') income += toCents(t.amount);
    if (t.type === 'expense') expenses += toCents(t.amount);
  }
  return { income: asMoney(income), expenses: asMoney(expenses),
    net: asMoney(income - expenses), transactions: entries.length };
}

export function monthlyBudgetCents(budget, asOf = new Date(), fiscalYearStart = '07-01') {
  const cents = toCents(budget.amount);
  if (budget.period !== 'custom') {
    if (budget.period === 'monthly') return cents;
    if (budget.period === 'yearly' || budget.period === 'quarterly') {
      // Allocate every cent across the fiscal period, without losing cents
      // when the amount cannot be divided evenly into months.
      const fiscalStartMonth = Number(budget.fiscalStartMonth || String(fiscalYearStart).slice(0,2) || 7);
      if (!Number.isInteger(fiscalStartMonth) || fiscalStartMonth < 1 || fiscalStartMonth > 12)
        throw new Error('Invalid fiscal start month');
      const month = Number(calendarDate(asOf).slice(5,7));
      const monthOffset = (month - fiscalStartMonth + 12) % 12;
      const months = budget.period === 'yearly' ? 12 : 3;
      const periodIndex = monthOffset % months;
      const absolute = Math.abs(cents);
      const base = Math.floor(absolute / months);
      return Math.sign(cents) * (base + (periodIndex < absolute % months ? 1 : 0));
    }
    return cents;
  }
  const start = calendarDate(budget.startDate);
  const end = calendarDate(budget.endDate);
  if (!start || !end || end < start) return 0; // Legacy invalid custom periods never imply a monthly cap.
  const month = calendarDate(asOf).slice(0,7);
  const [year, number] = month.split('-').map(Number);
  const monthStart = month + '-01';
  const monthEnd = new Date(Date.UTC(year, number, 0)).toISOString().slice(0,10);
  const from = start > monthStart ? start : monthStart;
  const to = end < monthEnd ? end : monthEnd;
  if (from > to) return 0;
  const totalDays = countCoverageDays(start,end);
  const offset = countCoverageDays(start,from) - 1;
  const covered = countCoverageDays(from,to);
  const base = Math.floor(Math.abs(cents)/totalDays);
  const remainder = Math.abs(cents)%totalDays;
  const extra = Math.max(0,Math.min(remainder,offset+covered)-Math.min(remainder,offset));
  return Math.sign(cents)*(base*covered+extra);
}

export function budgetSummary(budgets, entries, asOf = new Date(), fiscalYearStart = '07-01') {
  const today = calendarDate(asOf);
  const current = today.slice(0,7);
  const recognized = entries.filter(t => calendarDate(t.date).startsWith(current) &&
    calendarDate(t.date) <= today &&
    (t.isDailyBreakdown || t.status === 'posted') && t.type === 'expense');
  return budgets.filter(b => !b.isDeleted).map(b => {
    const allocated = monthlyBudgetCents(b,asOf,fiscalYearStart);
    const custom = b.period === 'custom';
    const validCustom = !custom || (calendarDate(b.startDate) &&
      calendarDate(b.endDate) && calendarDate(b.startDate) <= calendarDate(b.endDate));
    const spent = recognized.filter(t => String(t.category) === String(b.category) &&
      (!custom || validCustom && calendarDate(t.date) >= calendarDate(b.startDate) &&
        calendarDate(t.date) <= calendarDate(b.endDate)))
      .reduce((sum,t)=>sum+toCents(t.amount),0);
    return { ...b, monthlyBudget:asMoney(allocated), spent:asMoney(spent),
      remaining:asMoney(Math.max(0,allocated-spent)),
      percentage:allocated>0?(spent/allocated)*100:0,
      needsDates:!validCustom, isOverBudget:validCustom && allocated>0 && spent>allocated };
  });
}

function iouEffectCents(entry, currentBalanceCents) {
  const cents = toCents(entry.amount);
  switch (entry.type) {
    case 'you_lent': case 'lent': return cents;
    case 'you_borrowed': case 'borrowed': return -cents;
    case 'repayment_received': return -cents;
    case 'repayment_made': return cents;
    // Legacy generic repayment has no direction field; apply against
    // the existing balance rather than incorrectly always subtracting it.
    case 'repayment':
      return currentBalanceCents > 0 ? -cents : currentBalanceCents < 0 ? cents : 0;
    default: return 0;
  }
}

export function personLedger(person, transactions, ious, asOf = new Date()) {
  const today = calendarDate(asOf);
  const ids = new Set(ious.filter(i => i.personId === person.id && !i.isDeleted &&
    i.linkedTransactionId != null).map(i => String(i.linkedTransactionId)));
  const raw = transactions.filter(t => valid(t) && t.status === 'posted' &&
    calendarDate(t.date) <= today &&
    (t.person === person.name || String(t.person) === String(person.id)) &&
    !t.isDailyBreakdown && !t.linkedIOUId && !t.iouId && !ids.has(String(t.id)))
    .map(t => ({ ...t, entryType: 'transaction' }));
  raw.push(...ious.filter(i => !i.isDeleted && String(i.personId) === String(person.id) &&
    calendarDate(i.date) <= today).map(i => ({ ...i, entryType: 'iou' })));
  raw.sort((a,b) => calendarDate(a.date).localeCompare(calendarDate(b.date)) ||
    Number(a.id || 0) - Number(b.id || 0));
  let balanceCents = 0;
  const ledgerEntries = raw.map(entry => {
    const effect = entry.entryType === 'transaction'
      ? (entry.type === 'expense' ? toCents(entry.amount) : -toCents(entry.amount))
      : iouEffectCents(entry, balanceCents);
    balanceCents += effect;
    return { ...entry, balanceEffect: asMoney(effect),
      runningBalance: asMoney(balanceCents) };
  });
  return { currentBalance: asMoney(balanceCents), ledgerEntries };
}